import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, TextField, Select, MenuItem, Stack, Alert } from '@mui/material';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadSimulations, createSimulation, updateSimulation } from './simLabSlice';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = { scheduled: tokens.severity.info, running: tokens.severity.medium, completed: tokens.severity.low, cancelled: tokens.text.muted };
const STATUS_OPTIONS = ['scheduled', 'running', 'completed', 'cancelled'];
const SCENARIO_TYPES = ['PHISHING_SIMULATION', 'BREACH_SIMULATION', 'TABLETOP_EXERCISE', 'RED_TEAM_EXERCISE'];

export default function SimLabPage() {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.simLab);
  const [name, setName] = useState('');
  const [scenarioType, setScenarioType] = useState('PHISHING_SIMULATION');
  const [targetGroup, setTargetGroup] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { dispatch(loadSimulations()); }, [dispatch]);

  const scheduled = items.filter((s) => s.status === 'scheduled').length;
  const completed = items.filter((s) => s.status === 'completed').length;

  const handleSchedule = async () => {
    if (busy) return;
    if (!name.trim()) return setError('Exercise name is required.');
    let scheduledAt = new Date();
    if (scheduledFor) {
      scheduledAt = new Date(scheduledFor);
      if (Number.isNaN(scheduledAt.getTime())) return setError('Enter a valid date and time for this exercise.');
    }
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await dispatch(createSimulation({ name: name.trim(), scenarioType, targetGroup: targetGroup.trim() || null, scheduledAt: scheduledAt.toISOString() }));
      if (createSimulation.rejected.match(result)) {
        setError(result.payload || result.error?.message || 'Could not schedule this exercise.');
        return;
      }
      setName('');
      setTargetGroup('');
      setScheduledFor('');
      setNotice(`"${result.payload?.name || name.trim()}" was scheduled.`);
      // Reconcile with the server after creation so a successful schedule can never
      // disappear because of stale client state or response-shape drift.
      await dispatch(loadSimulations());
    } finally {
      setBusy(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setError(null);
    const result = await dispatch(updateSimulation({ id, changes: { status } }));
    if (updateSimulation.rejected.match(result)) setError(result.payload || result.error?.message || 'Could not update this session.');
  };

  return (
    <Box>
      <PageHeader
        title="Simulation & Validation Lab"
        subtitle="SOC exercise scheduling — phishing simulations, breach simulations, tabletop exercises, and red team engagements"
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total Sessions" value={items.length} icon={ScienceRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Scheduled" value={scheduled} accent={tokens.severity.info} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Completed" value={completed} accent={tokens.severity.low} /></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          <TextField size="small" fullWidth placeholder="Exercise name" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField size="small" fullWidth placeholder="Target group (e.g. Finance department or All staff)" value={targetGroup} onChange={(e) => setTargetGroup(e.target.value)} />
          <Select size="small" value={scenarioType} onChange={(e) => setScenarioType(e.target.value)} sx={{ minWidth: 220 }}>
            {SCENARIO_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </Select>
          <TextField size="small" type="datetime-local" label="Scheduled for" InputLabelProps={{ shrink: true }} value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} sx={{ minWidth: 210 }} helperText="Leave blank for now" />
          <Button variant="contained" onClick={handleSchedule} disabled={busy}>{busy ? 'Scheduling…' : 'Schedule'}</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Scenario', 'Target Group', 'Status', 'Scheduled'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontSize: '0.8rem' }}>{s.name}</TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{String(s.scenarioType || '').replace(/_/g, ' ')}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{s.targetGroup || '—'}</TableCell>
                <TableCell>
                  <Select
                    size="small" variant="standard" value={s.status} disableUnderline
                    onChange={(e) => handleStatusChange(s.id, e.target.value)}
                    sx={{ fontSize: '0.76rem', color: STATUS_COLOR[s.status] }}
                  >
                    {STATUS_OPTIONS.map((opt) => <MenuItem key={opt} value={opt} sx={{ fontSize: '0.78rem' }}>{opt}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{s.scheduledAt ? new Date(s.scheduledAt).toLocaleString() : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
